// Creator-facing planning-failure copy — a CLOSED switch over known reasons.
//
// WHY THIS EXISTS — 2026-07-27 incident. `mapPlanningFailureMessage` in
// hero-chat-input.tsx collapsed every typed failure except ontology-blocked
// into one generic string, throwing away per-reason copy that
// features/chat/services/live-planner-copy.ts already authors in five
// locales. A creator whose request timed out, whose plan lost the scope of
// the brief, and whose planner was simply down all read the same sentence.
//
// The hard constraint the old code existed to enforce is preserved exactly:
// NOTHING here can render raw internal text. `resolveCreatorPlanningFailure`
// only ever returns a member of a closed union (or `null`), and
// `creatorPlanningFailureMessage` is total over that union — every branch
// returns authored copy. An unrecognized message maps to `null`, which the
// caller renders as the generic string, which is the pre-existing behavior.

import {
  livePlannerFailureMessage,
  type LivePlannerFailureReason,
} from '@/features/chat/services/live-planner-copy'
import { LOCALE_CODES, type LocaleCode } from '@/pages/cogo-marketing/i18n/locales'

/**
 * Reasons the hero can render distinct copy for. The six
 * `LivePlannerFailureReason` members reuse the copy already authored in
 * live-planner-copy.ts; the other two are authored in the studio dictionary
 * (`strings.chat.planningFailure`) because they are hero-surface copy.
 */
export type CreatorPlanningFailureReason =
  | 'ontology-blocked'
  | 'plan-not-actionable'
  | 'requirement-coverage'
  | LivePlannerFailureReason

/** The subset of `strings.chat.planningFailure` this module needs. */
export interface CreatorPlanningFailureCopy {
  ontologyBlocked: string
  planNotActionable: string
  requirementCoverage: string
  generic: string
}

/**
 * Admission failures whose cause is the SHAPE OF THE PLAN, not a missing
 * domain in the library.
 *
 * Measured 2026-08-19: a plan whose blocks carry no binds and no datasources
 * scores 0.90 against ACTIONFLOW_APPLICATION_ARCHETYPE_CORPUS (threshold
 * 1.2), so archetype retrieval finds nothing and admission refuses. The same
 * plan with one bind and two datasources scores 2.00 and is admitted. Adding
 * a domain to the library changes neither number.
 *
 * Matched by PREFIX rather than an enumerated list on purpose: the codes are
 * the producer's vocabulary (`chat-ontology-generation-quality.ts` emits
 * `actionflow-compiler-proof-invalid`, `actionflow-runtime-proof-invalid`,
 * `actionflow-readiness-not-ready`), and a new `actionflow-*` code should
 * route here the day it is added rather than falling back to copy that names
 * the wrong cause. Any other code keeps the pre-existing behaviour.
 */
const PLAN_SHAPE_ISSUE_CODE_PREFIX = 'actionflow-'

/**
 * The producer's fail-closed ontology rejection. Matched by substring on the
 * raw message exactly as before this module existed — the ontology-blocked
 * behavior is deliberately byte-for-byte unchanged.
 */
const ONTOLOGY_BLOCKED_MARKER = 'Ontology admission blocked'

const LIVE_PLANNER_REASONS: readonly LivePlannerFailureReason[] = [
  'configuration',
  'invalid-response',
  'scope-mismatch',
  'static-unsupported',
  'timeout',
  'unavailable',
]

/**
 * Reverse index of the authored live-planner strings -> their reason, built
 * once from live-planner-copy.ts itself across every supported locale.
 *
 * This is what makes the routing closed AND honest: the only non-ontology
 * messages it recognizes are strings this app authored. The producer
 * (`start-app-generation-from-prompt.ts`) throws `new Error(fallbackReason)`
 * where `fallbackReason` is exactly `livePlannerFailureMessage(reason)` in
 * the creator's locale, so an exact match recovers the reason with no
 * heuristics, no substring sniffing of model text, and no way for an
 * arbitrary internal message to slip through.
 */
const AUTHORED_MESSAGE_REASONS: ReadonlyMap<string, LivePlannerFailureReason> =
  (() => {
    const index = new Map<string, LivePlannerFailureReason>()
    for (const locale of LOCALE_CODES) {
      for (const reason of LIVE_PLANNER_REASONS) {
        index.set(livePlannerFailureMessage(reason, locale), reason)
      }
    }
    return index
  })()

/**
 * The proxy's machine reason codes (`X-Cogo-Decompose-Failure-Reason`, see
 * functions/api/decompose-failure-reason.ts) mapped onto creator copy.
 *
 * Codes with no honest creator-facing counterpart map to `null` -> generic:
 * `bad-request` / `unauthorized` / `aborted` / `unknown` are either handled
 * elsewhere (auth expiry goes through `handleProjectCreateError`) or carry no
 * action a creator could take, and inventing one would be worse than the
 * generic line.
 *
 * NOT YET REACHED FROM THE LIVE PATH — see `resolveCreatorPlanningFailure`.
 */
const DECOMPOSE_REASON_COPY: Readonly<
  Record<string, CreatorPlanningFailureReason | null>
> = {
  'requirement-coverage': 'requirement-coverage',
  // The plan copied an unrelated example domain — from the creator's side
  // that is the same experience as losing the scope of the brief.
  'seed-contamination': 'scope-mismatch',
  'invalid-response': 'invalid-response',
  'not-configured': 'configuration',
  timeout: 'timeout',
  'upstream-error': 'unavailable',
  'bad-request': null,
  unauthorized: null,
  aborted: null,
  unknown: null,
}

/**
 * Map a decompose proxy reason code onto creator copy. Unknown or
 * non-actionable codes return `null` (-> generic).
 */
export function creatorReasonFromDecomposeFailureReason(
  code: string | null | undefined,
): CreatorPlanningFailureReason | null {
  if (!code) return null
  return DECOMPOSE_REASON_COPY[code] ?? null
}

/**
 * Classify a producer failure message into a creator reason, or `null` when
 * it is not one this app authored.
 *
 * `decomposeFailureReason` is the proxy's typed code
 * (`X-Cogo-Decompose-Failure-Reason`). It takes precedence when supplied
 * because it is strictly more specific than the message.
 *
 * HONEST LIMITATION: no in-scope caller supplies it today.
 * `app-spec-from-prompt.ts` collapses every HTTP 422 to the
 * `invalid-response` copy before the hero ever sees it, so a
 * requirement-coverage rejection currently arrives here as the
 * `invalid-response` string and renders that copy. Forwarding the header from
 * the producer is a one-line follow-up in
 * `features/chat/services/app-spec-from-prompt.ts`, deliberately outside this
 * slice; the moment it lands, `requirement-coverage` routes here with no
 * further change.
 */
export function resolveCreatorPlanningFailure(
  rawMessage: string,
  decomposeFailureReason?: string | null,
  ontologyIssueCodes?: readonly string[],
): CreatorPlanningFailureReason | null {
  const typed = creatorReasonFromDecomposeFailureReason(decomposeFailureReason)
  if (typed) return typed
  if (rawMessage.includes(ONTOLOGY_BLOCKED_MARKER)) {
    // Structured codes win over the prose when the producer supplied them.
    // Without this the substring match collapses every ontology rejection
    // into `ontology-blocked`, whose copy tells the creator to ask their team
    // to add a domain — which does not move the score that actually blocked.
    return ontologyIssueCodes?.some((code) =>
      code.startsWith(PLAN_SHAPE_ISSUE_CODE_PREFIX),
    )
      ? 'plan-not-actionable'
      : 'ontology-blocked'
  }
  return AUTHORED_MESSAGE_REASONS.get(rawMessage) ?? null
}

/**
 * Reason -> authored creator copy. Total over the closed union: every branch
 * returns copy authored in this repo, never the caught message.
 */
export function creatorPlanningFailureMessage(
  reason: CreatorPlanningFailureReason,
  locale: LocaleCode,
  copy: CreatorPlanningFailureCopy,
): string {
  switch (reason) {
    case 'ontology-blocked':
      return copy.ontologyBlocked
    case 'plan-not-actionable':
      return copy.planNotActionable
    case 'requirement-coverage':
      return copy.requirementCoverage
    default:
      return livePlannerFailureMessage(reason, locale)
  }
}

/**
 * The whole mapping in one call: a raw producer message (plus, when a caller
 * has it, the proxy's typed reason) becomes creator-safe copy. Anything
 * unrecognized becomes `copy.generic` — the pre-existing fallback.
 */
export function mapCreatorPlanningFailureMessage(
  rawMessage: string,
  locale: LocaleCode,
  copy: CreatorPlanningFailureCopy,
  decomposeFailureReason?: string | null,
  ontologyIssueCodes?: readonly string[],
): string {
  const reason = resolveCreatorPlanningFailure(
    rawMessage,
    decomposeFailureReason,
    ontologyIssueCodes,
  )
  return reason === null
    ? copy.generic
    : creatorPlanningFailureMessage(reason, locale, copy)
}
